import React from "react";
import { Link } from "react-router-dom";
import { Plane, Home, FileCheck, Users, Wrench, Sprout, ArrowUpRight } from "lucide-react";

const IMG_HERO = "https://images.pexels.com/photos/2382665/pexels-photo-2382665.jpeg?auto=compress&cs=tinysrgb&w=1400";

const services = [
  { icon: FileCheck, title: "Contract sourcing", body: "We negotiate direct US farm & homestead contracts. You see the paper, we see the paper." },
  { icon: Plane, title: "Visa & immigration", body: "In-house partners handle H-1B, O-1, TN, and family visas from start to stamp." },
  { icon: Home, title: "Relocation & housing", body: "Flights, first-month housing, local orientation, and school info for families." },
  { icon: Users, title: "Overseas team match", body: "Every contract comes with a US team you'll actually work with — we introduce you before you fly." },
  { icon: Wrench, title: "Trade support", body: "Tool shipping, equipment sourcing, and US license/permit paperwork sorted." },
  { icon: Sprout, title: "Ongoing advocacy", body: "Your Berlin office stays your Berlin office — long after you've moved." },
];

export default function ServicesPage() {
  return (
    <div data-testid="services-page">
      <section className="relative overflow-hidden border-b border-[#DCD7C9]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-16 sm:py-24 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <div className="text-xs uppercase tracking-[0.28em] font-semibold text-[#D35400]">Services</div>
            <h1 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-semibold text-[#1A2E24]">
              Everything between here and there.
            </h1>
            <p className="mt-6 text-[#5C6B64] leading-relaxed max-w-xl">
              From your first Berlin coffee with our team to your first American paycheck —
              here's exactly what we handle.
            </p>
          </div>
          <div className="lg:col-span-6 relative aspect-[4/3] overflow-hidden border border-[#DCD7C9]">
            <img src={IMG_HERO} alt="Farm work" className="h-full w-full object-cover"/>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-24">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((s) => (
            <div key={s.title} className="border border-[#DCD7C9] bg-white p-8 hover:-translate-y-0.5 transition-transform">
              <s.icon className="text-[#D35400]" size={26} strokeWidth={1.5}/>
              <div className="font-display text-xl mt-5 text-[#1A2E24]">{s.title}</div>
              <p className="text-sm text-[#5C6B64] mt-2 leading-relaxed">{s.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Process */}
      <section className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-24 border-t border-[#DCD7C9]">
        <div className="text-xs uppercase tracking-[0.28em] font-semibold text-[#D35400]">How it works</div>
        <h2 className="mt-4 font-display text-3xl sm:text-4xl tracking-tight font-medium text-[#1A2E24] max-w-2xl">
          From Torstraße to a US farm in 90 days.
        </h2>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-4 gap-6">
          {[
            { n: "01", t: "You sign up", b: "Tell us your trade, experience, and where you'd like to go in the US." },
            { n: "02", t: "We match a farm", b: "We introduce you to a vetted US farm or homestead operator that fits." },
            { n: "03", t: "Paperwork done", b: "Visa, contract, and relocation packet handled by our team." },
            { n: "04", t: "Move & work", b: "You fly out with housing sorted, teammates waiting, and Berlin behind you." },
          ].map((s) => (
            <div key={s.n} className="border-t border-[#1A2E24] pt-6">
              <div className="font-display text-3xl text-[#D35400]">{s.n}</div>
              <div className="mt-3 font-display text-lg text-[#1A2E24]">{s.t}</div>
              <p className="text-sm text-[#5C6B64] mt-2 leading-relaxed">{s.b}</p>
            </div>
          ))}
        </div>
        <div className="mt-16">
          <Link data-testid="services-cta" to="/signup" className="inline-flex items-center gap-2 bg-[#1A2E24] text-[#F9F6F0] px-7 py-4 text-sm font-medium hover:bg-[#2C4A3B]">
            Start the process <ArrowUpRight size={16}/>
          </Link>
        </div>
      </section>
    </div>
  );
}
